// ============================================================
// TargetGearTab - Weapons, armour, and defensive gear display.
// Shows defender weapons with parry selection, current vs
// original AP, effective AP per location, force field,
// defensive traits, and cover. Body-type-aware armour display.
// ============================================================
import { memo, useMemo, type CSSProperties } from 'react';
import type { Target, DefenderWeapon } from '../types';
import { getBodyConfig } from '../engine/bodyConfig';
import Badge from './Badge';
import { TargetGearWeapons } from './TargetGearWeapons';
import { TargetGearArmour } from './TargetGearArmour';
import common from '../styles/common.module.css';
import styles from '../styles/TargetPanel.module.css';

interface Props {
  target: Target;
  onEditArmour: (locId: string) => void;
  onReorderWeapons?: (weapons: DefenderWeapon[]) => void;
  locked: boolean;
}

const EMPTY_NOTE: CSSProperties = { fontSize: 10, color: 'var(--text-faint)', textAlign: 'center', padding: 4 };

function TargetGearTab({ target, onEditArmour, onReorderWeapons, locked }: Props) {
  const bodyType = target.bodyType || (target.horde?.isHorde ? 'horde' : 'humanoid');
  const bodyConfig = useMemo(() => getBodyConfig(bodyType), [bodyType]);
  return (
    <div style={{ padding: '8px 0', overflow: 'auto' }}>
      <TargetGearWeapons weapons={target.defenderWeapons || []} onReorderWeapons={onReorderWeapons} locked={locked} />
      {bodyType !== 'horde' && (
        <TargetGearArmour
          target={target}
          bodyConfig={bodyConfig}
          isVehicle={bodyType === 'vehicle'}
          onEditArmour={onEditArmour}
          locked={locked}
        />
      )}
      <ForceFieldCard target={target} />
      <DefensiveTraitsCard target={target} />
      <CoverCard target={target} />
    </div>
  );
}

function ForceFieldCard({ target }: { target: Target }) {
  const ff = target.forceField;
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>FORCE FIELD</div>
      {ff?.active ? (
        <div className={common.overviewGrid} style={{ padding: 0 }}>
          <OverviewItem label="Protection" value={`${ff.protectionRating}%`} />
          <OverviewItem label="Overload" value={`${ff.overloadThreshold}+`} />
        </div>
      ) : (
        <div style={EMPTY_NOTE}>No active force field.</div>
      )}
    </div>
  );
}

// Declarative: each defensive trait's badge, short label and readout. A
// rated trait shows when above zero and reads its rating; a flag reads Active.
const DEFENSIVE_TRAITS: {
  key: 'naturalArmour' | 'machineAP' | 'daemonicProtection' | 'stuffOfNightmares' | 'incorporeal' | 'regeneration';
  badge: string;
  label: string;
  rated: boolean;
  suffix?: string;
}[] = [
  { key: 'naturalArmour', badge: 'Natural Armour', label: 'Natural Armour', rated: true },
  { key: 'machineAP', badge: 'Machine', label: 'Machine', rated: true },
  { key: 'daemonicProtection', badge: 'Daemonic', label: 'Daemonic', rated: true },
  { key: 'stuffOfNightmares', badge: 'Stuff of Nightmares', label: 'SoN', rated: false },
  { key: 'incorporeal', badge: 'Incorporeal', label: 'Incorporeal', rated: false },
  { key: 'regeneration', badge: 'Regeneration', label: 'Regen', rated: true, suffix: '/turn' },
];

function DefensiveTraitsCard({ target }: { target: Target }) {
  const shown = DEFENSIVE_TRAITS.filter((t) => (t.rated ? ((target[t.key] as number) || 0) > 0 : !!target[t.key]));
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>DEFENSIVE TRAITS</div>
      <div className={common.overviewGrid} style={{ padding: 0 }}>
        {shown.map((t) => (
          <DefensiveTrait key={t.key} trait={t} value={target[t.key]} />
        ))}
      </div>
      {!DEFENSIVE_TRAITS.some((t) => target[t.key]) && <div style={EMPTY_NOTE}>No defensive traits.</div>}
    </div>
  );
}

function DefensiveTrait({ trait, value }: { trait: (typeof DEFENSIVE_TRAITS)[number]; value: unknown }) {
  return (
    <div className={common.overviewItem}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {trait.rated ? (
          <Badge variant="trait" label={trait.badge} value={value as number} size={16} />
        ) : (
          <Badge variant="trait" label={trait.badge} size={16} />
        )}
        {trait.label}
      </span>
      {trait.rated ? (
        <span className={common.overviewValue}>
          +{value as number}
          {trait.suffix}
        </span>
      ) : (
        <span className={common.overviewValue} style={{ color: 'var(--ok)' }}>
          Active
        </span>
      )}
    </div>
  );
}

function CoverCard({ target }: { target: Target }) {
  const cover = target.cover;
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>COVER</div>
      {cover && cover.level !== 'none' ? (
        <div className={common.overviewGrid} style={{ padding: 0 }}>
          <div className={common.overviewItem}>
            <span>Level</span>
            <span className={common.overviewValue} style={{ textTransform: 'capitalize' }}>
              {cover.level}
            </span>
          </div>
          <OverviewItem label="AP" value={`+${cover.ap}`} />
        </div>
      ) : (
        <div style={EMPTY_NOTE}>No cover.</div>
      )}
    </div>
  );
}

function OverviewItem({ label, value }: { label: string; value: string }) {
  return (
    <div className={common.overviewItem}>
      <span>{label}</span>
      <span className={common.overviewValue}>{value}</span>
    </div>
  );
}

export default memo(TargetGearTab);
